import { AvatarDropdown, AvatarName, Question, SelectLang, ThemeToggle } from '@/components';
import { LinkOutlined, SearchOutlined } from '@ant-design/icons';
import { type Settings as LayoutSettings } from '@ant-design/pro-components';
import { Link, type RunTimeLayoutConfig } from '@umijs/max';
import { Input } from 'antd';
import React, { useState } from 'react';
import defaultSettings from '../config/defaultSettings';
import keycloak from '@/services/keycloak';
import { validateGuid } from '@/services/wfx';
import { isWfxAuthEnabled } from '@/utils/wfxAuth';
import {
  getWfxSession,
  mapSessionToCurrentUser,
  readGuidFromUrl,
  setWfxSession,
  stripGuidFromUrl,
} from '@/utils/wfxSession';
import SessionGuard from '@/wrappers/SessionGuard';
import { errorConfig } from './requestErrorConfig';
import './theme/design-tokens.css';


const isDev = process.env.NODE_ENV === 'development';

const STUB_USER = {
  name: 'John Doe',
  avatar: '',
  permissions: [] as any[],
};

// Convert camelCase name to readable words for search matching
// e.g. "materialType" → "material type"
const camelToWords = (str: string) =>
  str.replace(/([A-Z])/g, ' $1').trim().toLowerCase();

// Recursively filter menu items by search text.
// UmiJS processes routes into menu items with "children" (not "routes"),
// so we must check both to cover the raw routes config and the processed menu data.
const filterMenuData = (items: any[], search: string): any[] => {
  if (!search || !items) return items;
  const s = search.toLowerCase();

  return items.reduce((acc: any[], item: any) => {
    const nameLower = (item.name || '').toLowerCase();
    const nameWords = camelToWords(item.name || '');
    const pathLower = (item.path || '').toLowerCase();

    // UmiJS may use either "children" or "routes" for sub-items
    const subItems: any[] | undefined = item.children?.length
      ? item.children
      : item.routes?.length
        ? item.routes
        : undefined;

    if (subItems) {
      const filteredSubs = filterMenuData(subItems, search);
      if (filteredSubs.length > 0) {
        // Preserve both keys so ProLayout renders correctly regardless of version
        acc.push({ ...item, children: filteredSubs, routes: filteredSubs });
        return acc;
      }
    }

    // Leaf item: match against name key, readable name, or path segment
    if (
      nameLower.includes(s) ||
      nameWords.includes(s) ||
      pathLower.includes(s)
    ) {
      acc.push(item);
    }

    return acc;
  }, []);
};

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: any;
  loading?: boolean;
}> {
  if (!isWfxAuthEnabled()) {
    return {
      currentUser: STUB_USER,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }

  let session = getWfxSession();
  if (!session) {
    const guid = readGuidFromUrl();
    if (guid) {
      session = await validateGuid(guid);
      if (session) {
        setWfxSession(session);
        stripGuidFromUrl();
      }
    }
  }

  return {
    currentUser: session ? mapSessionToCurrentUser(session) : undefined,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }: any) => {
  const [collapsed, setCollapsed] = useState(false);
  const [searchText, setSearchText] = useState('');

  return {
    siderWidth: 200,
    breadcrumbRender: false,
    settingRender: false,
    menuRender: HIDE_MENU ? false : undefined,
    headerRender: HIDE_MENU ? false : undefined,
    collapsed,
    onCollapse: (value: boolean) => {
      setCollapsed(value);
      // Clear search when collapsing
      if (value) setSearchText('');
    },
    onPageChange: async () => {
      if (window.innerWidth < 768) {
        setCollapsed(true);
      }
    },
    // Search box rendered above menu items — full width, no horizontal padding
    menuExtraRender: ({ collapsed: isCollapsed }: { collapsed: boolean }) => {
      if (isCollapsed) return null;
      return (
        <div style={{ padding: '8px 0 4px', width: '100%' }}>
          <Input
            placeholder="Search menu..."
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            size="small"
            style={{ width: '100%', borderRadius: 0, border: 'none', borderBottom: '1px solid #f0f0f0' }}
          />
        </div>
      );
    },
    // Filter menu items based on search text
    menuDataRender: (menuData: any[]) => filterMenuData(menuData, searchText),
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
      <ThemeToggle key="theme" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_: any, avatarChildren: any) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    bgLayoutImgList: [
      {
        src: '/bg-1.webp',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: '/bg-2.webp',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: '/bg-3.webp',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
        <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
          <LinkOutlined />
          <span>OpenAPI Document</span>
        </Link>,
      ]
      : [],
    menuHeaderRender: undefined,
    childrenRender: (children: any) => {
      return <SessionGuard>{children}</SessionGuard>;
    },
    ...initialState?.settings,
  };
};

export const request = {
  ...errorConfig,
  requestInterceptors: [
    (url: string, options: any) => {
      const isFullUrl = /^http(s)?:\/\//.test(url);
      const finalUrl = isFullUrl ? url : `${API_URL}${url}`;
      const token = keycloak.token;
      return {
        url: finalUrl,
        options: {
          ...options,
          headers: {
            ...options.headers,
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        },
      };
    },
  ],
};
